<script setup lang="ts">
import { NuxtLink } from "#components";
import { type SideMenuItem } from "~/composables/useChromeMenuItems";

const props = defineProps({
  item: {
    type: Object as PropType<SideMenuItem>,
    required: true,
  },

  expandsOnItemClick: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits<{
  (event: "click"): void;
}>();

const isOpen = ref(false);

const warnings = computed(() => useMenuStore().warnings);
const numWarnings = computed(() => props.item.children?.reduce((acc, c) => acc + (warnings.value.find((w) => w.menuItem == c.type)?.count || 0), 0) ?? 0);

const blockNavigation = ref(false);

const clickIcon = (e: MouseEvent) => {
  e.stopImmediatePropagation();
  isOpen.value = !isOpen.value;
};

const clickHeader = (e: MouseEvent) => {
  e.stopImmediatePropagation();

  if (props.expandsOnItemClick) {
    isOpen.value = !isOpen.value;
    return;
  }

  emit("click");
};
</script>

<template>
  <NuxtLink :to="!blockNavigation ? props.item.route : undefined" :class="[$style.container]">
    <div :class="[$style.header, !isOpen && $style.closed, props.item.active.value && $style.active]" @click="(e) => clickHeader(e)">
      <KeyValuePair direction="horizontal">
        <template #containerStart>
          <Icon v-if="props.item.iconId" :id="props.item.iconId" size="medium" :color="props.item.active.value ? '--color-white' : '--color-accent-300'" />
        </template>

        <template #containerEnd>
          <SideMenuLabel :item="props.item" />
        </template>
      </KeyValuePair>

      <div style="flex: 1" />

      <Label v-if="numWarnings > 0" colorBackground="--color-red-100" colorForeground="--color-red-200">
        <template #copy>
          <Copy type="body" size="small" color="--color-red-300"> {{ numWarnings }} </Copy>
        </template>
      </Label>

      <div
        :class="[$style.icon, props.item.active.value && $style.iconActive]"
        @click="(e) => clickIcon(e)"
        @mouseover="blockNavigation = true"
        @mouseleave="blockNavigation = false"
      >
        <Icon :id="isOpen ? 'arrow_drop_up' : 'arrow_drop_down'" size="large" :color="props.item.active.value ? '--color-white' : '--color-accent-300'" />
      </div>
    </div>

    <transition
      :enter-active-class="$style.accordionEnterActive"
      :leave-active-class="$style.accordionEnterActive"
      :enter-from-class="$style.accordionEnterFrom"
      :leave-to-class="$style.accordionLeaveTo"
    >
      <div v-if="isOpen" :class="[$style.layout]">
        <slot name="container" />
      </div>
    </transition>
  </NuxtLink>
</template>

<style module>
.container {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  overflow: visible;
  border: none !important;
  text-decoration: none !important;
}

.header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.5rem 1rem;
  background-color: var(--color-transparent);
  cursor: pointer;
  transition: background-color var(--transition-duration-medium) ease;

  &:hover {
    background-color: var(--color-accent-1000);
  }
}

.active {
  background-color: var(--color-accent-1000);
}

.layout {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1px;
  max-height: 0px;
  overflow-y: auto;
  max-height: 300px;
}

.icon {
  display: flex;
  width: fit-content;
  margin-left: 0.5rem;
  transition: background-color var(--transition-duration-medium) ease;

  &:hover:not(.iconActive) {
    background-color: var(--color-accent-900);
  }

  &:hover.iconActive {
    background-color: var(--color-accent-900);
  }
}

.accordionEnterActive,
.accordionLeaveActive {
  transition: max-height var(--transition-duration-short) ease;
}

.accordionEnterFrom,
.accordionLeaveTo {
  max-height: 0;
}
</style>
